
export interface SalesIQVisitorInfo {
	name: string;
	email: string;
	phone?: string;
}

declare global {
	interface Window {
		$zoho: {
			salesiq: {
				ready: () => void;
				chat: { start: () => void };
				floatwindow: { visible: (state: "show" | "hide") => void };
				visitor: {
					name: (name: string) => void;
					email: (email: string) => void;
					contactnumber: (phone: string) => void;
				};
				floatbutton: { visible: (state: "show" | "hide") => void };
			};
		};
	}
}

class SalesIQService {
	private initPromise: Promise<void> | null = null;
	// Tenant-specific SalesIQ widget code (`wc`), fetched from the backend.
	private widgetCode: string | null = null;

	private load(): Promise<void> {
		if (this.initPromise) return this.initPromise;

		this.initPromise = new Promise<void>((resolve, reject) => {
			const baseUrl = process.env.REACT_APP_SALESIQ_WIDGET_BASE_URL;
			const scriptId = process.env.REACT_APP_SALESIQ_SCRIPT_ID;

			if (!baseUrl || !scriptId) {
				reject(new Error("SalesIQ env variables are not configured (REACT_APP_SALESIQ_WIDGET_BASE_URL, REACT_APP_SALESIQ_SCRIPT_ID)"));
				this.initPromise = null;
				return;
			}

			if (!this.widgetCode) {
				reject(new Error("SalesIQ widget code is not set — call init(widgetCode) first"));
				this.initPromise = null;
				return;
			}

			const scriptSrc = `${baseUrl}?wc=${this.widgetCode}`;

			// visibility:hidden keeps element dimensions intact so the widget
			// can calculate chat window position — display:none zeros them out and crashes.
			const style = document.createElement("style");
			style.textContent = `#zsiq_float { visibility: hidden !important; pointer-events: none !important; }`;
			document.head.appendChild(style);

			window.$zoho = window.$zoho || ({} as Window["$zoho"]);
			window.$zoho.salesiq = {
				...(window.$zoho.salesiq || {}),
				ready: () => resolve(),
			};

			const script = document.createElement("script");
			script.id = scriptId;
			script.src = scriptSrc;
			script.defer = true;
			script.onerror = () => {
				this.initPromise = null;
				reject(new Error("Failed to load SalesIQ script"));
			};
			document.body.appendChild(script);
		});

		return this.initPromise;
	}

	init(widgetCode: string): void {
		this.widgetCode = widgetCode;
		this.load().catch(() => {});
	}

	async openChat(visitor?: SalesIQVisitorInfo): Promise<void> {
		await this.load();

		if (visitor) {
			if (visitor.name) window.$zoho.salesiq.visitor.name(visitor.name);
			if (visitor.email) window.$zoho.salesiq.visitor.email(visitor.email);
			if (visitor.phone) window.$zoho.salesiq.visitor.contactnumber(visitor.phone);
		}

		// chat.start() crashes because the chat iframe is created lazily.
		// Clicking the float button triggers Zoho's own handlers which handle
		// lazy init correctly. Programmatic .click() bypasses visibility:hidden.
		document.getElementById("zs_fl_chat")?.click();
	}

	closeChat(): void {
		if (!this.initPromise) return;
		window.$zoho?.salesiq?.floatwindow?.visible("hide");
	}
}

export const salesIQService = new SalesIQService();
